import React from 'react';
import { parseDocument } from 'htmlparser2';
import { Text, View, Link, StyleSheet, Image, Font } from '@react-pdf/renderer';

Font.register({
    family: 'Inter',
    fonts: [
        { src: `${process.env.PUBLIC_URL}/font/Inter-Regular.ttf`, fontWeight: 400 },
        { src: `${process.env.PUBLIC_URL}/font/Inter-Italic.ttf`, fontWeight: 400, fontStyle: 'italic' },
        { src: `${process.env.PUBLIC_URL}/font/Inter-Medium.ttf`, fontWeight: 500 },
        { src: `${process.env.PUBLIC_URL}/font/Inter-MediumItalic.ttf`, fontWeight: 500, fontStyle: 'italic' },
        { src: `${process.env.PUBLIC_URL}/font/Inter-SemiBold.ttf`, fontWeight: 600 },
        { src: `${process.env.PUBLIC_URL}/font/Inter-SemiBoldItalic.ttf`, fontWeight: 600, fontStyle: 'italic' },
        { src: `${process.env.PUBLIC_URL}/font/Inter-Bold.ttf`, fontWeight: 700 },
        { src: `${process.env.PUBLIC_URL}/font/Inter-BoldItalic.ttf`, fontWeight: 700, fontStyle: 'italic' }
    ]
});

Font.registerHyphenationCallback(word => [word]);

const styles_img = StyleSheet.create({
    categoryText: {
        paddingLeft: '0%',
        paddingRight: '5%',
        marginTop: 45,
    },
    inclImage: {
        width: '200px',
        height: '233px',
    },
    inclImageText: {
        display: 'block',
        fontFamily: 'Inter',
        fontSize: 10,
        fontWeight: 500,
        lineHeight: 1.4,
        textAlign: 'right',
        paddingTop: 6,
        paddingBottom: 6,
    },
    inclImageText2: {
        display: 'block',
        fontFamily: 'Inter',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.4,
        textAlign: 'center',
        paddingTop: 10,
    }
});

const getDynamicStyles = (coverData) => StyleSheet.create({
    paragraph: { fontFamily: 'Inter', fontSize: coverData?.["introText-paragraph"] || 9, fontWeight: 400, marginBottom: 10 },
    bold: { fontFamily: 'Inter', fontWeight: 700 },
    italic: { fontFamily: 'Inter', fontStyle: 'italic', fontWeight: 400 },
    link: { fontFamily: 'Inter', fontWeight: 400, color: '#006cb7', textDecoration: 'underline' },
    h1: { fontFamily: 'Inter', fontSize: coverData?.["introText-h1"] || 20, color: coverData?.["introText-h1-color"] || "black", fontWeight: 700, marginBottom: 12 },
    h2: { fontFamily: 'Inter', fontSize: coverData?.["introText-h2"] || 15, color: coverData?.["introText-h2-color"] || "black", fontWeight: 700, marginBottom: 10, marginTop: 8 },
    h3: { fontFamily: 'Inter', fontSize: coverData?.["introText-h3"] || 12, color: coverData?.["introText-h3-color"] || "black", fontWeight: 600, marginBottom: 8 },
    h4: { fontFamily: 'Inter', fontSize: coverData?.["introText-h4"] || 11, color: coverData?.["introText-h4-color"] || "black", fontWeight: 600, marginBottom: 6 },
    h5: { fontFamily: 'Inter', fontSize: coverData?.["introText-h5"] || 10, color: coverData?.["introText-h5-color"] || "black", fontWeight: 600, marginBottom: 6 },
    ul: { marginLeft: 20, marginBottom: 10 },
    li: { fontFamily: 'Inter', fontWeight: 400, fontSize: coverData?.["introText-list"] || 9, marginBottom: 4 },
});

const renderNode = (node, styles) => {
    if (node.type === 'text') {
        return <Text>{node.data}</Text>;
    }

    switch (node.name) {
        case 'p':
            return <Text wrap={false} style={styles.paragraph}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'b':
            return <Text style={styles.bold}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'i':
            return <Text style={styles.italic}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'a':
            return (
                <Link style={styles.link} src={node.attribs.href}>
                    {node.children.map(child => renderNode(child, styles))}
                </Link>
            );
        case 'br':
            return <Text>{'\n'}</Text>;
        case 'h1':
            return <Text wrap={false} style={styles.h1}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'h2':
            return <Text wrap={false} style={styles.h2}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'h3':
            return <Text wrap={false} style={styles.h3}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'h4':
            return <Text wrap={false} style={styles.h4}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'h5':
            return <Text wrap={false} style={styles.h5}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'ul':
            return <View wrap={false} style={styles.ul}>{node.children.map(child => renderNode(child, styles))}</View>;
        case 'li':
            return <Text wrap={false} style={styles.li}>• {node.children.map(child => renderNode(child, styles))}</Text>;
        default:
            return <Text>{node.children.map(child => renderNode(child, styles))}</Text>;
    }
};

const renderHTMLToPDF = (htmlString, styles) => {
    if (!htmlString || typeof htmlString !== 'string') {
        return [<Text>No content to display</Text>];
    }
    const parsedHTML = parseDocument(htmlString);
    return parsedHTML.children.map(node => renderNode(node, styles));
};

const IntroText = ({ coverData, uiData, typePDF }) => {
    if (!coverData || !coverData.introText) {
        return null;
    }

    const styles = getDynamicStyles(coverData);

    return (
        <>

            {typePDF === 'guide' ? (
                <View style={styles_img.categoryText}>
                    <View style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    }}>
                        <View style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            alignContent: 'flex-end',
                            alignItems: 'flex-end',
                            textAlign: 'right',
                            gap: 2,
                            paddingTop: 70,
                        }}>
                            <Text style={styles_img.inclImageText}>
                                {uiData.imgText01}
                            </Text>
                            <Text style={styles_img.inclImageText}>
                                {uiData.imgText02}
                            </Text>
                            <Text style={styles_img.inclImageText}>
                                {uiData.imgText03}
                            </Text>
                        </View>
                        <View style={{
                            width: '200',
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                        }}>
                            <Image src={`${process.env.PUBLIC_URL}/image-guide.png`} style={styles_img.inclImage} />
                            <Text style={styles_img.inclImageText2}>
                                {uiData.imgText04}
                            </Text>
                        </View>
                    </View>
                </View>
            ) : (
                <View style={{
                    position: 'relative',
                    height: 450,
                    width: '100%',
                    overflow: 'hidden',
                }}>
                    <Image
                        src={`${process.env.PUBLIC_URL}/introText-image.jpg`}
                        style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'cover',
                            objectPosition: 'top center',
                        }}
                    />
                </View>
            )}


            <View style={{
                position: 'relative',
                height: 50,
                width: '100%',
            }} fixed />

            <View
                style={{
                    position: 'relative',
                    paddingLeft: '9%',
                    paddingRight: '9%',
                    marginTop: -20,
                    width: '100%',
                }}
            >
                {renderHTMLToPDF(coverData.introText, styles)}
            </View>

            <View style={{
                position: 'relative',
                height: 30,
                width: '100%',
            }} fixed />
        </>
    );
};

export default IntroText;
